import { useEffect, useRef, useState } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Palette, Trash2, Save, Image as ImageIcon, Globe } from "lucide-react";
import { useProtectedAssetUrl } from "@/hooks/useProtectedAssetUrl";

export default function SettingsBranding() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [header, setHeader] = useState("");
  const [footer, setFooter] = useState("");
  const [color, setColor] = useState("#4F46E5");
  const [website, setWebsite] = useState("");
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/branding");
      setConfig(data);
      setHeader(data.header_text || "");
      setFooter(data.footer_text || "");
      setColor(data.accent_color || "#4F46E5");
      setWebsite(data.website || "");
    } catch (err) { toast.error(getApiErrorMessage(err, "Errore caricamento")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/branding", { header_text: header, footer_text: footer, accent_color: color, website });
      toast.success("Branding salvato");
      load();
    } catch (err) { toast.error(getApiErrorMessage(err, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (fileObj) => {
    if (!fileObj) return;
    if (fileObj.size > 3 * 1024 * 1024) { toast.error("Logo troppo grande (max 3 MB)"); return; }
    const form = new FormData();
    form.append("file", fileObj);
    try {
      await api.post("/settings/branding/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo caricato");
      setLogoCacheBust(Date.now());
      load();
    } catch (err) { toast.error(getApiErrorMessage(err, "Errore upload")); }
  };

  const removeLogo = async () => {
    try { await api.delete("/settings/branding/logo"); toast.success("Logo rimosso"); load(); }
    catch { toast.error("Errore"); }
  };

  const { assetUrl: logoUrl } = useProtectedAssetUrl(config?.has_logo ? `/branding/${config.tenant_id}/logo?v=${logoCacheBust}` : null, !!config?.has_logo);

  if (loading) return <div className="p-10 text-slate-500">Caricamento…</div>;

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 mb-2">Impostazioni · White-label</div>
        <h1 className="font-display font-black text-4xl md:text-5xl">Branding & Verbali</h1>
        <p className="text-slate-500 mt-2 text-sm max-w-2xl leading-relaxed">
          Personalizza logo, intestazione e footer che compariranno nei verbali generati. Il branding è per <strong>{config?.tenant_name}</strong> (P.IVA <span className="font-mono-plex">{config?.tenant_vat}</span>) — visibile a tutti gli utenti del tenant.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left: config */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="h-5 w-5 text-indigo-600" />
              <h2 className="font-display font-bold text-xl">Logo</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  data-testid="logo-input"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
                />
                <Button onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn" className="h-10 bg-indigo-600 hover:bg-indigo-700">
                  <Upload className="h-4 w-4" /> {config?.has_logo ? "Sostituisci logo" : "Carica logo"}
                </Button>
                {config?.has_logo && (
                  <Button onClick={removeLogo} variant="outline" data-testid="remove-logo-btn" className="h-10 ml-2 text-red-700 border-red-300 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" /> Rimuovi
                  </Button>
                )}
                <div className="mt-2 text-[11px] text-slate-500">PNG, JPG, WEBP o SVG · max 3 MB · consigliato: sfondo trasparente, ratio orizzontale</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-indigo-600" />
              <h2 className="font-display font-bold text-xl">Aspetto</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Colore d'accento</Label>
                <div className="flex items-center gap-2 mt-2">
                  <input type="color" data-testid="color-input" value={color} onChange={(e) => setColor(e.target.value)} className="h-11 w-14 rounded-md border border-slate-300 cursor-pointer" />
                  <Input value={color} onChange={(e) => setColor(e.target.value)} className="h-11 font-mono-plex flex-1" placeholder="#4F46E5" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Sito web</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Globe className="h-4 w-4 text-slate-400" />
                  <Input data-testid="website-input" value={website} onChange={(e) => setWebsite(e.target.value)} className="h-11" placeholder="https://esempio.it" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-display font-bold text-xl mb-4">Intestazione verbale</h2>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Testo intestazione (max 800 caratteri)</Label>
            <Textarea data-testid="header-input" value={header} onChange={(e) => setHeader(e.target.value)} rows={4} className="mt-2" placeholder="Es. Studio Rossi & Partners — Consulenza compliance | Via Roma 1, Milano | dpo@studiorossi.it" />
            <div className="mt-1.5 text-[11px] text-slate-500">Compare in alto nel verbale generato dal tuo OpenAI, sopra al titolo.</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-display font-bold text-xl mb-4">Footer verbale</h2>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Testo footer (max 800 caratteri)</Label>
            <Textarea data-testid="footer-input" value={footer} onChange={(e) => setFooter(e.target.value)} rows={3} className="mt-2" placeholder="Es. © 2026 Studio Rossi & Partners — Documento riservato al management. Riproduzione vietata." />
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-branding-btn" className="h-11 bg-indigo-600 hover:bg-indigo-700 px-6">
              <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva branding"}
            </Button>
          </div>
        </div>

        {/* Right: preview */}
        <div className="md:col-span-1">
          <div className="sticky top-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Anteprima</div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              <div className="p-5 border-b-4" style={{ borderColor: color }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" data-testid="logo-preview" className="max-h-16 max-w-full object-contain" />
                ) : (
                  <div className="h-16 flex items-center justify-center text-slate-300 text-xs">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}
                {header && (
                  <div className="mt-3 text-[11px] text-slate-600 whitespace-pre-wrap leading-relaxed">{header}</div>
                )}
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color }}>Verbale di chiusura audit</div>
                <div className="font-display font-black text-slate-900 leading-tight">Nome Azienda · v1</div>
                <div className="text-[10px] text-slate-400 mt-1">Anteprima layout — i dati reali dell'audit compariranno qui.</div>
              </div>
              {footer && (
                <div className="p-4 border-t border-slate-100 text-[10px] text-slate-500 whitespace-pre-wrap">{footer}</div>
              )}
            </div>
            {website && (
              <div className="mt-3 text-[11px] text-slate-500 text-center">
                <a href={website} target="_blank" rel="noreferrer" className="hover:text-indigo-700">{website}</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
